import React, { useMemo, useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faAnglesRight, faSliders, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { utcToLADateKey } from '../../utils/laDateUtils';
import { derivePeriodsFromDataGrid } from '../../utils/dataGridPeriods';
import MissionDatesSection, { DateRangeRow } from './MissionDatesSection';
import '../css/ParticipantDetailsSidebar.css';

/**
 * Same calendar-day rule as Booking tab (ParticipantBookingTab.formatDate):
 * convert the stored instant to an America/Los_Angeles date key, then format.
 * Browser-local getDate() and ISO-date-part shortcuts both disagree with Booking.
 */
const parseMissionDate = (value) => {
  if (!value) return null;
  const key = utcToLADateKey(value);
  if (!key) return null;
  const [y, m, d] = key.split('-').map(Number);
  if (!y || !m || !d) return null;
  const localNoon = new Date(y, m - 1, d, 12, 0, 0, 0);
  return {
    day:     String(d).padStart(2, '0'),
    month:   new Intl.DateTimeFormat('en-GB', { month: 'short' }).format(localNoon),
    year:    y,
    weekday: new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(localNoon),
  };
};

/** Same span math as Booking tab nightsBetween — end minus start, no +1. */
const periodDayCount = (from, to) => {
  const a = new Date(from);
  const b = new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const days = Math.round((b.getTime() - a.getTime()) / 86400000);
  return days < 0 ? null : days;
};

/**
 * Prefer person_data.dataGrid (Form.io source of truth) over typed period arrays.
 * Periods can lag after an optimistic save, or stay frozen from older saves.
 */
export const resolveParticipantDatePeriods = (participant) => {
  const dataGrid = participant?.person_data?.dataGrid;
  if (Array.isArray(dataGrid)) {
    const { arrival, stay } = derivePeriodsFromDataGrid(dataGrid);
    return { arrivalPeriods: arrival, stayPeriods: stay };
  }
  return {
    arrivalPeriods: participant?.arrival_departure_periods || [],
    stayPeriods: participant?.stay_periods || [],
  };
};

/**
 * Mission range shown in Summary → Dates.
 * Reads only participants.admin_plan mission start and end.
 * Missing values stay empty. Travel and stay dates are separate blocks.
 */
export const resolveMissionDates = (participant) => ({
  from: participant?.admin_plan?.mission_start_date || null,
  to: participant?.admin_plan?.mission_end_date || null,
});

const FieldBlock = ({ label, children }) => (
  <label className="participant-details-sidebar__field">
    <span className="participant-details-sidebar__label">{label}</span>
    {children}
  </label>
);

const ReadValue = ({ value, statusValue }) => (
  <div
    className="participant-details-sidebar__value"
    {...(statusValue
      ? { 'data-status': statusValue.toLowerCase().replace(/\s+/g, '-') }
      : {})}
  >
    {value || '—'}
  </div>
);

const REG_OPTIONS = [
  { value: 'Added',      label: 'Added',      dot: 'added' },
  { value: 'Pending',    label: 'Pending',     dot: 'pending' },
  { value: 'Registered', label: 'Registered',  dot: 'registered' },
];
const APPROVAL_OPTIONS = [
  { value: 'Pending',  label: 'Pending Approval', dot: 'pending' },
  { value: 'Approved', label: 'Approved',          dot: 'approved' },
  { value: 'Rejected', label: 'Rejected',           dot: 'rejected' },
];

/** Fully custom-styled dropdown — replaces native <select> */
const SidebarSelect = ({ value, onChange, options, placeholder, statusValue, searchable = false }) => {
  const [open, setOpen]     = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [query, setQuery]   = useState('');
  const ref        = useRef(null);
  const triggerRef = useRef(null);
  const inputRef   = useRef(null);

  const handleToggle = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setOpenUp(window.innerHeight - rect.bottom < 220);
    }
    setOpen((v) => !v);
  };

  useEffect(() => {
    if (!open) { setQuery(''); return; }
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const esc   = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    if (searchable) setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open, searchable]);

  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const selected = options.find((o) => o.value === value);
  const label    = selected?.label ?? placeholder ?? '—';
  const dot      = statusValue?.toLowerCase().replace(/\s+/g, '-');

  return (
    <div ref={ref} className={`pds-select${open ? ' pds-select--open' : ''}${openUp ? ' pds-select--up' : ''}`}>
      <button
        ref={triggerRef}
        type="button"
        className="pds-select__trigger"
        onClick={handleToggle}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {dot && <span className={`pds-select__dot pds-select__dot--${dot}`} />}
        <span className="pds-select__label">{label}</span>
        <svg className="pds-select__chevron" viewBox="0 0 20 20" fill="none">
          <path d="M5 7.5l5 5 5-5" stroke="currentColor" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className={`pds-select__menu${openUp ? ' pds-select__menu--up' : ''}`}>
          {searchable && (
            <div className="pds-select__search-wrap">
              <svg className="pds-select__search-icon" viewBox="0 0 20 20" fill="none">
                <circle cx="8.5" cy="8.5" r="5" stroke="#a67c2c" strokeWidth="1.6"/>
                <path d="M13 13l3 3" stroke="#a67c2c" strokeWidth="1.6" strokeLinecap="round"/>
              </svg>
              <input
                ref={inputRef}
                type="text"
                className="pds-select__search"
                placeholder="Search…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
              />
            </div>
          )}
          <ul role="listbox">
            {filtered.length === 0 && (
              <li className="pds-select__option pds-select__option--empty">No results</li>
            )}
            {filtered.map((o) => (
              <li
                key={o.value}
                role="option"
                aria-selected={o.value === value}
                className={`pds-select__option${o.value === value ? ' pds-select__option--active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); onChange(o.value); setOpen(false); }}
              >
                {o.dot && <span className={`pds-select__dot pds-select__dot--${o.dot}`} />}
                {o.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const ParticipantDetailsSidebar = ({
  open,
  onClose,
  mode = 'user',
  registrationStatus = '',
  approvalStatus = '',
  groupId = '',
  roleId = '',
  invitationTypeId = '',
  accreditationCategoryId = '',
  groups = [],
  roles = [],
  invitationTypes = [],
  accreditationCategories = [],
  registrationLabel = '',
  approvalLabel = '',
  groupLabel = '',
  roleLabel = '',
  invitationLabel = '',
  accreditationLabel = '',
  formatAccreditationOption,
  onRegistrationChange,
  onApprovalChange,
  onGroupChange,
  onRoleChange,
  onInvitationChange,
  onAccreditationChange,
  missionFrom = null,
  missionTo = null,
  onMissionDatesSave,
  arrivalPeriods = [],
  stayPeriods = [],
  onSave,
  saving = false,
  saveError = '',
}) => {
  const isAdmin = mode === 'admin';
  const [activeTab, setActiveTab] = useState('details');
  const missionFrom_p = useMemo(() => parseMissionDate(missionFrom), [missionFrom]);
  const missionTo_p   = useMemo(() => parseMissionDate(missionTo),   [missionTo]);
  const missionDays   = useMemo(
    () => (missionFrom && missionTo ? periodDayCount(missionFrom, missionTo) : null),
    [missionFrom, missionTo]
  );

  return (
      <aside
        className={`participant-details-sidebar${open ? ' participant-details-sidebar--open' : ''}`}
        aria-labelledby="participant-details-sidebar-title"
        aria-hidden={!open}
        inert={open ? undefined : ''}
      >
        <header className="participant-details-sidebar__header">
          <div className="participant-details-sidebar__heading">
            <h2 id="participant-details-sidebar-title">Summary</h2>
          </div>
          <button
            type="button"
            className="participant-details-sidebar__collapse"
            onClick={onClose}
            aria-label="Close details"
          >
            <FontAwesomeIcon icon={faAnglesRight} />
          </button>
        </header>

        {/* Tab strip */}
        <div className="pds-tabstrip" role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === 'details'}
            className={`pds-tabstrip__tab${activeTab === 'details' ? ' pds-tabstrip__tab--active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            <FontAwesomeIcon icon={faSliders} aria-hidden="true" />
            Assignment
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'dates'}
            className={`pds-tabstrip__tab${activeTab === 'dates' ? ' pds-tabstrip__tab--active' : ''}`}
            onClick={() => setActiveTab('dates')}
          >
            <FontAwesomeIcon icon={faCalendarDays} aria-hidden="true" />
            Dates
          </button>
        </div>

        <div className="participant-details-sidebar__body">
          <div className="participant-details-sidebar__body-scroll">

            {/* ── Details tab ── */}
            {activeTab === 'details' && (isAdmin ? (
              <>
                <FieldBlock label="Registration Status">
                  <SidebarSelect
                    value={registrationStatus || ''}
                    onChange={(v) => onRegistrationChange?.(v)}
                    options={REG_OPTIONS}
                    statusValue={registrationStatus}
                  />
                </FieldBlock>
                <FieldBlock label="Approval Status">
                  <SidebarSelect
                    value={approvalStatus || 'Pending'}
                    onChange={(v) => onApprovalChange?.(v)}
                    options={APPROVAL_OPTIONS}
                    statusValue={approvalStatus}
                  />
                </FieldBlock>
                <FieldBlock label="Group">
                  <SidebarSelect
                    value={groupId || ''}
                    onChange={(v) => onGroupChange?.(v)}
                    placeholder="Select Group"
                    options={groups.map((g) => ({ value: g._id, label: g.group_name }))}
                    searchable
                  />
                </FieldBlock>
                <FieldBlock label="Role">
                  <SidebarSelect
                    value={roleId || ''}
                    onChange={(v) => onRoleChange?.(v)}
                    placeholder="Select Role"
                    options={roles.map((r) => ({ value: r._id, label: r.role_name }))}
                    searchable
                  />
                </FieldBlock>
                <FieldBlock label="Invitation Type">
                  <SidebarSelect
                    value={invitationTypeId || ''}
                    onChange={(v) => onInvitationChange?.(v)}
                    placeholder="Select Invitation Type"
                    options={invitationTypes.map((t) => ({ value: t._id, label: t.name }))}
                    searchable
                  />
                </FieldBlock>
                <FieldBlock label="Accreditation Category">
                  <SidebarSelect
                    value={accreditationCategoryId || ''}
                    onChange={(v) => onAccreditationChange?.(v)}
                    placeholder="Select Accreditation Category"
                    options={accreditationCategories.map((c) => ({
                      value: c._id,
                      label: formatAccreditationOption
                        ? formatAccreditationOption(c)
                        : c.category_name || c.name || c._id,
                    }))}
                    searchable
                  />
                </FieldBlock>
              </>
            ) : (
              <>
                <FieldBlock label="Registration Status">
                  <ReadValue value={registrationLabel || registrationStatus} statusValue={registrationStatus} />
                </FieldBlock>
                <FieldBlock label="Approval Status">
                  <ReadValue value={approvalLabel || approvalStatus} statusValue={approvalStatus} />
                </FieldBlock>
                <FieldBlock label="Group">
                  <ReadValue value={groupLabel} />
                </FieldBlock>
                <FieldBlock label="Role">
                  <ReadValue value={roleLabel} />
                </FieldBlock>
                <FieldBlock label="Invitation Type">
                  <ReadValue value={invitationLabel} />
                </FieldBlock>
                <FieldBlock label="Accreditation Category">
                  <ReadValue value={accreditationLabel} />
                </FieldBlock>
              </>
            ))}

            {/* ── Dates tab ── */}
            {activeTab === 'dates' && (
              <div className="pds-dates" aria-label="Mission dates">
                {!isAdmin && !missionFrom && arrivalPeriods.length === 0 && stayPeriods.length === 0 ? (
                  <div className="pds-dates__empty">
                    <FontAwesomeIcon icon={faCalendarDays} aria-hidden="true" />
                    <p>No dates recorded yet.</p>
                  </div>
                ) : (
                  <>
                    {(isAdmin || missionFrom || missionTo) && (
                      <MissionDatesSection
                        editable={isAdmin && typeof onMissionDatesSave === 'function'}
                        hasDates={Boolean(missionFrom || missionTo)}
                        fromDisplay={missionFrom_p}
                        toDisplay={missionTo_p}
                        days={missionDays}
                        startKey={missionFrom ? utcToLADateKey(missionFrom) : ''}
                        endKey={missionTo ? utcToLADateKey(missionTo) : ''}
                        onSave={onMissionDatesSave}
                      />
                    )}

                    {/* Travel periods */}
                    {arrivalPeriods.length > 0 && (
                      <div className="pds-dates__section">
                        <div className="pds-dates__section-title">
                          <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                          Travel
                        </div>
                        {arrivalPeriods.map((p, i) => {
                          const f = parseMissionDate(p.arrival_date);
                          const t = parseMissionDate(p.departure_date);
                          const n = f && t ? periodDayCount(p.arrival_date, p.departure_date) : null;
                          return (
                            <DateRangeRow key={i} fromLabel="Arrival" toLabel="Departure" from={f} to={t} days={n} />
                          );
                        })}
                      </div>
                    )}

                    {/* Accommodation periods */}
                    {stayPeriods.length > 0 && (
                      <div className="pds-dates__section">
                        <div className="pds-dates__section-title">
                          <FontAwesomeIcon icon={faCalendarDays} aria-hidden="true" />
                          Accommodation
                        </div>
                        {stayPeriods.map((p, i) => {
                          const f = parseMissionDate(p.check_in_date);
                          const t = parseMissionDate(p.check_out_date);
                          const n = f && t ? periodDayCount(p.check_in_date, p.check_out_date) : null;
                          return (
                            <DateRangeRow key={i} fromLabel="Check-in" toLabel="Check-out" from={f} to={t} days={n} />
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

          </div>
        </div>

        <footer className="participant-details-sidebar__footer">
          {saveError ? (
            <div className="participant-details-sidebar__error" role="alert">
              {saveError}
            </div>
          ) : null}
          {isAdmin && saving ? (
            <span className="participant-details-sidebar__saving-indicator">Saving…</span>
          ) : null}
        </footer>
      </aside>
  );
};

export default ParticipantDetailsSidebar;
